// src/lib/MerchantModel.js
// ─── Firestore data models and helpers for the merchant platform ─────────────
import {
  doc, collection, setDoc, getDoc, getDocs, updateDoc,
  query, where, serverTimestamp,
} from 'firebase/firestore';
import { db } from './firebase';

// ─────────────────────────────────────────────────────────────────────────────
// MERCHANT DOCUMENT  merchants/{merchantId}
// ─────────────────────────────────────────────────────────────────────────────
//
// {
//   id:            string          — auto-generated Firestore ID (NOT the user's UID)
//   ownerUid:      string          — Firebase Auth UID of the merchant owner
//   name:          string          — merchant / business name
//   email:         string
//   phone:         string | null
//   address:       string | null
//   logoUrl:       string | null
//   plan:          'silver' | 'gold' | 'platinum'
//   tableCount:    number
//   createdAt:     Timestamp
//   updatedAt:     Timestamp
// }

export const createMerchant = async (uid, { name, email, phone = null, address = null }) => {
  const ref  = doc(collection(db, 'merchants')); // auto-generated ID
  const data = {
    id:         ref.id,
    ownerUid:   uid,               // ← link back to Firebase Auth user
    name,
    email,
    phone,
    address,
    logoUrl:    null,
    plan:       'silver',
    tableCount: 0,
    createdAt:  serverTimestamp(),
    updatedAt:  serverTimestamp(),
  };
  await setDoc(ref, data);
  return { ...data, id: ref.id };
};

// Query by ownerUid — merchant doc ID is NOT the user's UID
export const getMerchant = async (uid) => {
  const q    = query(collection(db, 'merchants'), where('ownerUid', '==', uid));
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const d = snap.docs[0];
  return { id: d.id, ...d.data() };
};

export const updateMerchant = async (merchantId, fields) => {
  await updateDoc(
    doc(db, 'merchants', merchantId),
    { ...fields, updatedAt: serverTimestamp() },
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// TABLE DOCUMENT  merchants/{merchantId}/tables/{tableId}
// ─────────────────────────────────────────────────────────────────────────────

export const createTable = async (merchantId, label) => {
  const tablesRef = collection(db, 'merchants', merchantId, 'tables');
  const tableRef  = doc(tablesRef);
  const qrToken   = generateQrToken(tableRef.id);
  const qrUrl     = buildQrUrl(merchantId, qrToken);

  const data = {
    id:         tableRef.id,
    merchantId,
    label,
    qrToken,
    qrUrl,
    active:     true,
    createdAt:  serverTimestamp(),
  };
  await setDoc(tableRef, data);

  // Keep merchant.tableCount in sync
  const merchantSnap = await getDoc(doc(db, 'merchants', merchantId));
  if (merchantSnap.exists()) {
    await updateDoc(doc(db, 'merchants', merchantId), {
      tableCount: (merchantSnap.data().tableCount || 0) + 1,
      updatedAt:  serverTimestamp(),
    });
  }

  return data;
};

export const getTable = async (merchantId, tableId) => {
  const snap = await getDoc(doc(db, 'merchants', merchantId, 'tables', tableId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

export const getTableByToken = async (merchantId, qrToken) => {
  const q    = query(
    collection(db, 'merchants', merchantId, 'tables'),
    where('qrToken', '==', qrToken),
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const d = snap.docs[0];
  return { id: d.id, ...d.data() };
};

// ─────────────────────────────────────────────────────────────────────────────
// SESSION DOCUMENT  merchants/{merchantId}/sessions/{sessionId}
// ─────────────────────────────────────────────────────────────────────────────

export const createSession = async (merchantId, tableId, tableLabel) => {
  const sessionsRef = collection(db, 'merchants', merchantId, 'sessions');
  const sessionRef  = doc(sessionsRef);

  const data = {
    id:          sessionRef.id,
    merchantId,
    tableId,
    tableLabel,
    status:      'waiting',
    receipt:     null,
    guests:      [],
    partySize:   0,
    totalAmount: 0,
    createdAt:   serverTimestamp(),
    completedAt: null,
  };
  await setDoc(sessionRef, data);
  return data;
};

export const updateSession = async (merchantId, sessionId, fields) => {
  await updateDoc(
    doc(db, 'merchants', merchantId, 'sessions', sessionId),
    fields,
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// QR TOKEN HELPERS
// ─────────────────────────────────────────────────────────────────────────────

const generateQrToken = (firestoreId) => firestoreId.slice(0, 8).toLowerCase();

export const buildQrUrl = (merchantId, qrToken) =>
  `https://checkie.app/t/${merchantId}/${qrToken}`;

// ─────────────────────────────────────────────────────────────────────────────
// PLAN LIMITS
// ─────────────────────────────────────────────────────────────────────────────

export const PLAN_LIMITS = {
  silver:   { sessionsPerMonth: 100,      tables: 5,        label: 'Silver' },
  gold:     { sessionsPerMonth: 500,      tables: 20,       label: 'Gold' },
  platinum: { sessionsPerMonth: Infinity, tables: Infinity, label: 'Platinum' },
};

export const isWithinPlanLimit = (plan, sessionCountThisMonth) => {
  const limit = PLAN_LIMITS[plan]?.sessionsPerMonth ?? 100;
  return sessionCountThisMonth < limit;
};